import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { AppHeaderBar, HeaderTitle } from "./app-header";
import DateRangeTabs, { type DateRangeOption } from "./date-range-tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "./tooltip";

export const PageHeader = ({
  title,
  layout = "page",
  showDateRangeTabs = false,
  dateRangeOptions,
  dateRangeSource,
  enableTimeSelection,
  docsLink,
  leftComp,
  rightComp,
  noPattern = false,
  dateRangeTabsClassName,
}: {
  title?: ReactNode;
  /** `page`: full-width band with the dot pattern (px-4 md:px-8). `analytics`: plain grid row on the content inset (px-6). */
  layout?: "page" | "analytics";
  showDateRangeTabs?: boolean;
  dateRangeOptions?: DateRangeOption[];
  dateRangeSource?: string;
  enableTimeSelection?: boolean;
  docsLink?: string;
  leftComp?: ReactNode;
  rightComp?: ReactNode;
  noPattern?: boolean;
  dateRangeTabsClassName?: string;
}) => {
  const patternClasses = noPattern
    ? ""
    : "bg-[image:radial-gradient(var(--pattern_color)_0.5px,_transparent_0)] bg-[size:16px_16px] [--pattern_color:var(--color-black)]/40 dark:[--pattern_color:var(--color-white)]/25";

  const dateRangeTabs = showDateRangeTabs ? (
    <DateRangeTabs
      dateRangeOptions={dateRangeOptions}
      source={dateRangeSource}
      enableTimeSelection={enableTimeSelection}
      containerClassName={cn("h-[28px]", dateRangeTabsClassName)}
    />
  ) : null;

  if (layout === "analytics") {
    return (
      <div>
        <AppHeaderBar
          height="min"
          inset="content"
          className={cn(
            // gap-y-3 covers the rare wrap to two rows.
            "grid gap-x-4 gap-y-3 bg-app-background",
            title && dateRangeTabs
              ? "grid-cols-[minmax(0,1fr)_auto]"
              : "grid-cols-1",
          )}
        >
          {title ? (
            <HeaderTitle as="div" className="min-w-0">
              {title}
            </HeaderTitle>
          ) : null}
          {dateRangeTabs && (
            <div
              className={cn(
                "shrink-0 justify-self-end",
                !title && "col-start-1 row-start-1",
              )}
            >
              {dateRangeTabs}
            </div>
          )}
        </AppHeaderBar>
      </div>
    );
  }

  return (
    <div>
      <AppHeaderBar
        as="header"
        height="min"
        inset="page"
        className={cn(
          "relative justify-center bg-app-background",
          noPattern ? "" : "py-0.5",
          patternClasses,
        )}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {leftComp ? (
            // Same 1px optical nudge as the title path so leftComp headers
            // (e.g. job/run detail breadcrumbs) sit on the sidebar's line too.
            <div className="-translate-y-px flex min-w-0 flex-1 items-start gap-1 md:items-center">
              {leftComp}
            </div>
          ) : (
            <HeaderTitle
              as="div"
              className="flex-1 flex gap-6 whitespace-nowrap"
            >
              <div className="flex items-center gap-2">
                {title}
                {docsLink && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={docsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center cursor-pointer text-muted-foreground hover:text-foreground rounded-md border border-transparent focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="size-4"
                          >
                            <title>Documentation</title>
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                          </svg>
                          <span className="sr-only">Documentation</span>
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        Learn more in our{" "}
                        <a
                          href={docsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline cursor-pointer"
                        >
                          docs
                        </a>
                        .
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </HeaderTitle>
          )}
          <div className="flex items-center">{rightComp || dateRangeTabs}</div>
        </div>
      </AppHeaderBar>
    </div>
  );
};
